import { Box, Collapse, IconButton, Stack, useTheme } from "@mui/material";
import { getScorePrecision } from "src/context/Scores/provider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useMyActionPlanContext } from "../../../context/useMyActionPlan";
import { getImpact } from "../utils";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { ActionPlanIndicatorBreakdownPopover, FoodsIcon, Typography } from "@foodpilot/foods";
import { ImpactComparisonTables } from "../ImpactComparisonTables/ImpactComparisonTables";
import { useState } from "react";
import { SummaryValues } from "../../../context/utils/impactData/formatImpactSummary";
import { formatNumberByType } from "src/utils/formatting";
import { useLanguage } from "src/context/LanguageProvider";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { formatImpactWithExactValue, getScoreImpactFormatOptions } from "src/context/Scores/utils/impactDisplay";
import { getImpactIndicatorBreakdown, getImpactIndicatorCount } from "../impactBreakdown";
import { Score } from "src/types";

export const ImpactSectionDrawer = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { scoresWithVariations, formatScore, getColor } = useEntityScores();
    const { currentLanguage } = useLanguage();
    const { scoresEnabled } = useActionsContext();
    const { actionPlanAction, impactDataSummary } = useMyActionPlanContext();

    const [expanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded(!expanded);
    };

    const summaryData = impactDataSummary.summary.summary;
    const oneSimpleImpactData = impactDataSummary.simpleImpactTable?.totalRowCount === 1;
    const simpleImpact = oneSimpleImpactData ? summaryData.find((impact) => impact.type === "simple") : undefined;

    const withExtended = oneSimpleImpactData === false;

    const getScoreUnit = (score: Score): string => {
        const variation = scoresWithVariations.find((row) => row.scoreId === score.id);
        return variation?.unit ?? score.unit?.abbreviation ?? "";
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.primary[100],
                borderRadius: "12px",
                padding: theme.spacing(2),
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                <Stack direction="row" alignItems="center" spacing={2} flexShrink={0}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FoodsIcon size={4} icon={"wand"} />
                    </Box>
                    <Typography variant="h3" sx={{ color: theme.palette.text.primary }}>
                        {t("action_plan.estimated_impact", "Estimated impact")}
                    </Typography>
                </Stack>

                <Box sx={{ overflowX: "auto", flex: 1, minWidth: 0 }}>
                    <Stack
                        component="ul"
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={1.5}
                        sx={{ listStyle: "none", margin: 0, padding: 0, minWidth: "100%", width: "max-content" }}
                    >
                        {simpleImpact && <SimpleImpactCard impact={simpleImpact} />}
                        {scoresEnabled.map((score) => {
                            const impact = getImpact(actionPlanAction, score.id);
                            const precision = getScorePrecision(score);
                            const color = getColor(impact, { improvement: score.improvement, precision });
                            const unit = getScoreUnit(score);
                            const indicatorCount = getImpactIndicatorCount(actionPlanAction, score.id);
                            const breakdown = getImpactIndicatorBreakdown(actionPlanAction, score.id);

                            return (
                                <Box
                                    component="li"
                                    key={score.id}
                                    sx={{
                                        backgroundColor: theme.custom.grey[100],
                                        borderRadius: theme.spacing(1),
                                        padding: theme.spacing(1),
                                        flexShrink: 0,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Stack alignItems="flex-start" justifyContent="center">
                                        <Typography variant="small-regular" color={theme.custom.grey[2000]}>
                                            {score.title}
                                        </Typography>
                                        <Stack direction="row" alignItems="flex-end" spacing={0.5}>
                                            {impact === null || impact === undefined ?
                                                <Typography variant="body-regular">
                                                    {t("action_plan.action.non_quantifiable", "Not quantifiable")}
                                                </Typography>
                                            :   <ApproximateImpactTooltip
                                                    value={impact}
                                                    precision={precision}
                                                    unit={unit}
                                                    sx={{ alignItems: "flex-end", gap: theme.spacing(0.5) }}
                                                >
                                                    <Typography variant="number-small" sx={{ color }}>
                                                        {formatScore(impact, { precision })}
                                                    </Typography>
                                                    <Typography
                                                        variant="body-regular"
                                                        sx={{ color: theme.custom.grey[2000] }}
                                                    >
                                                        {unit}
                                                    </Typography>
                                                </ApproximateImpactTooltip>
                                            }
                                            {indicatorCount > 1 && impact !== null && (
                                                <ActionPlanIndicatorBreakdownPopover
                                                    title={t(
                                                        "action_plan.action.indicator_breakdown_title",
                                                        "Breakdown by indicator",
                                                    )}
                                                    ariaLabel={t(
                                                        "action_plan.action.indicator_breakdown_aria",
                                                        "Show breakdown by indicator",
                                                    )}
                                                    lines={breakdown.map((line) => ({
                                                        label: line.label,
                                                        formattedValue: formatImpactWithExactValue(
                                                            line.score,
                                                            precision,
                                                            currentLanguage,
                                                            unit,
                                                            getScoreImpactFormatOptions(precision),
                                                        ),
                                                        color: getColor(line.score, {
                                                            improvement: score.improvement,
                                                            precision,
                                                        }),
                                                    }))}
                                                    totalLabel={t("total", "Total")}
                                                    totalFormattedValue={formatScore(impact, { precision }) ?? ""}
                                                    totalUnit={unit}
                                                    totalColor={color}
                                                />
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                {withExtended && (
                    <IconButton
                        onClick={toggleExpand}
                        sx={{
                            p: 0,
                            backgroundColor: "transparent",
                            flexShrink: 0,
                            "&:hover": { backgroundColor: "transparent" },
                        }}
                        disableRipple
                    >
                        {expanded ?
                            <ArrowUpIcon sx={{ fontSize: 24, color: theme.custom.grey[2800] }} />
                        :   <ArrowDownIcon sx={{ fontSize: 24, color: theme.custom.grey[2800] }} />}
                    </IconButton>
                )}
            </Stack>
            {!oneSimpleImpactData && (
                <Collapse in={expanded}>
                    <Stack sx={{ mt: 2 }}>
                        <ImpactComparisonTables />
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
};

type SimpleImpactCardProps = {
    impact: Extract<SummaryValues, { type: "simple" }>;
};
const SimpleImpactCard = (props: SimpleImpactCardProps) => {
    const { impact } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();

    return (
        <Box
            component="li"
            sx={{
                backgroundColor: theme.palette.primary[50],
                borderRadius: theme.spacing(1),
                padding: theme.spacing(1),
                flexShrink: 0,
                whiteSpace: "nowrap",
            }}
        >
            <Stack alignItems="flex-start" justifyContent="center">
                <Typography variant="small-regular" color={theme.custom.grey[2000]}>
                    {impact.label}
                </Typography>
                <Stack direction="row" alignItems="flex-end" spacing={0.5}>
                    {impact.value === null ?
                        <Typography variant="number-small">{t("glossary.no_value", "No value")}</Typography>
                    :   <Typography variant="number-small" color={theme.custom.grey[2500]}>
                            {formatNumberByType(impact.value, impact.unit, currentLanguage, 2, false)}
                        </Typography>
                    }
                    <Typography variant="body-regular" color={theme.custom.grey[2000]}>
                        {impact.unit}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};
